import { Injectable, inject } from '@angular/core';
import { Observable, catchError, first, map, mergeMap, of, throwError } from 'rxjs';
import { NOT_FOUND_LABEL } from 'src/app/config/app.config';
import { AppContextService } from 'src/app/shared/services/app-context/app-context.service';
import { generateIndexDBKey } from 'src/app/shared/utils/indexdb.utils';
import { DeviceService } from '../../entities/device/device.service';
import { EVENT_BUFFER_PROTOCOL } from '../../entities/event-buffer/enums/event-protocol.enum';
import { TICKET_ERROR } from '../../enums/errors/ticket-error.enum';
import { HTTP_ACCOUNT_EVENT } from '../../enums/events-buffer/endpoints/http-account-event.enum';
import { SOCKET_AUTHOR_MODEL } from '../../enums/events-buffer/socket/socket-author-model.enum';
import { SOCKET_TICKET_ENTRY } from '../../enums/events-buffer/socket/socket-ticket-event.enum';
import { Account } from '../../entities/account/domain/model/account.model';
import { IAudit } from '../../interfaces/audit.interface';
import { Logger } from '../../interfaces/Logger';
import { SocketErrorResponse } from '../../interfaces/socket-error.model';
import { ISocketPayload } from '../../interfaces/socket-payload.interface';
import { ISocketResponse } from '../../interfaces/socket-response.interface';
import { SocketService } from '../socket/socket.service';
import { HTTP_METHOD } from '../../enums/http-methods.enum';
import { environment } from 'src/environments/environment';
import { AccountApiMapper } from '../../entities/account/infrastructure/mappers/account-api.mapper';
import { AuditMapper } from '../../entities/audit/infrastructure/mapper/audit.mapper';
import { EntryMapper } from '../../entities/entry/infrastructure/mapper/entry.mapper';
import { TicketMapper } from '../../entities/ticket/infrastructure/mapper/ticket.mapper';
import { Payment } from '../../entities/payment/domain/model/payment.model';
import { PaymentMapper } from '../../entities/payment/domain/mappers/payment.mapper';
import { Ticket } from '../../entities/ticket/domain/model/ticket.model';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Worker } from '@features/service/domain/models/worker.model';

/**
 * Service responsible for handling socket communication related to payment operations
 * including sending account payment events and ticket events through sockets.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentsSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly deviceService = inject(DeviceService);
  private readonly logger = inject(Logger);
  private readonly appContextService = inject(AppContextService);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  /**
   * Sends the event when an account is closed
   *
   * @param {Account} account - The account being paid
   * @param {Worker | null} worker - The worker associated with the payment
   * @param {IPayment} payment - Payment information
   * @param {IAudit | null} currentAudit - Current audit information
   * @param {boolean} [closed] - Whether the account is closed
   * @param {Ticket | null} [ticket] - Associated ticket information
   * @returns {Observable<ISocketResponse>} Observable with socket response
   */
  sendPayAccountAmountEvent(
    account: Account | null,
    worker: Worker | null,
    payment: Payment,
    currentAudit: IAudit | null,
    closed?: boolean,
    ticket?: Ticket | null,
  ): Observable<ISocketResponse> {
    if (!ticket?.account_id) {
      ticket = null;
    }

    if (!account) {
      const error = new Error(
        'Error sending pay account amount event without account',
      );

      this.logger.error(error);

      return throwError(() => error);
    }

    return this.socketService.prepareToEmitEvent<ISocketResponse>({
      _id: generateIndexDBKey(),
      name: HTTP_ACCOUNT_EVENT.pay,
      payload: {
        closed,
        account: AccountApiMapper.toApi(account),
        payment: PaymentMapper.toApi(payment),
        ticket: ticket ? TicketMapper.toJSON(ticket) : null,
        audits: currentAudit ? [AuditMapper.toApi(currentAudit)] : [],
        entries: payment?.entries?.map((entry) => EntryMapper.toApi(entry)) || [],
      },
      protocol: EVENT_BUFFER_PROTOCOL.http,
      http: {
        method: HTTP_METHOD.POST,
        url: `${environment.apiURL}${HTTP_ACCOUNT_EVENT.pay}`,
      },
    });
  }

  /**
   * Sends a ticket event with email information to socket
   *
   * @param {string} email - Email address to send the ticket to
   * @param {Account | null} account - Associated account information
   * @param {Ticket | null} ticket - Ticket information to be sent
   * @returns {Observable<boolean>} Observable that emits true if successful
   */
  sendTicketEvent(
    email: string,
    account: Account | undefined,
    ticket: Ticket | null,
  ): Observable<boolean> {
    return this.getPayloadData().pipe(
      mergeMap((payloadData) => {
        const payload: ISocketPayload = {
          negocio_id: account?.negocio_id || '',
          action: SOCKET_TICKET_ENTRY.send,
          author: {
            authorId: payloadData.currentWorker?.id ?? '',
            model: SOCKET_AUTHOR_MODEL.waiter,
          },
          device: this.appContextService.deviceName,
          organization: this.appContextService.organizationName,
          serviceId: this.appContextService.serviceId,
          timestamp: Date.now(),
          payload: {
            ticket: ticket?.serie ?? NOT_FOUND_LABEL,
            email: email,
          },
        };

        return this.socketService
          .prepareToEmitEvent<boolean>({
            _id: generateIndexDBKey(),
            name: SOCKET_TICKET_ENTRY.send,
            payload,
            protocol: EVENT_BUFFER_PROTOCOL.socket,
          })
          .pipe(first());
      }),
      catchError((error) => {
        throw new SocketErrorResponse(TICKET_ERROR.sendTicket, error);
      }),
    );
  }

  /**
   * Retrieves current worker information needed for socket payload construction
   *
   * @private
   * @returns {Observable<{currentWorker: Worker | null}>} Observable with current worker data
   */
  private getPayloadData(): Observable<{
    currentWorker: Worker | null;
  }> {
    return of(this.serviceStateService.getCurrentWorker()).pipe(
      first(),
      map((currentWorker: Worker | null) => {
        return {
          currentWorker,
        };
      }),
    );
  }
}
